import dayjs from "dayjs"
import { formatDateString } from "src/utils/format-date"
import { formatDecimalSeparator } from "src/v2/utils/formatter/format-number"
import { formatSinal } from "src/v2/utils/formatter/format-sinal"

const formatDate = (value) => formatDateString(value, "-")

const sortByDate = (rows, field = "DataReferencia") => [...rows].sort((first, second) => {
    const firstDate = dayjs(first?.[field])
    const secondDate = dayjs(second?.[field])
    const firstTimestamp = firstDate.isValid() ? firstDate.valueOf() : Number.NEGATIVE_INFINITY
    const secondTimestamp = secondDate.isValid() ? secondDate.valueOf() : Number.NEGATIVE_INFINITY

    if (secondTimestamp === firstTimestamp) return 0
    return secondTimestamp > firstTimestamp ? 1 : -1
})

const toNumericGridValue = (value) => {
    if (value === null || value === undefined || (typeof value === "string" && !value.trim())) return null

    const numericValue = Number(value)
    return Number.isFinite(numericValue) ? numericValue : null
}

const formatPdGridValue = ({ value, row }) => formatDecimalSeparator(row?.PD ?? value)

const getRows = (response) => response?.body?.results ?? []

const HISTORY_METADATA_FIELDS = new Set(["ID", "id", "CriadoEm", "AtualizadoEm"])

const omitHistoryMetadata = (row) => Object.keys(row).reduce((payload, field) => {
    if (!HISTORY_METADATA_FIELDS.has(field)) payload[field] = row[field]
    return payload
}, {})

const copyLatestHistoryRow = (rows) => rows[0] ? omitHistoryMetadata(rows[0]) : null

const ratingFields = [
    { name: "DataReferencia", label: "Data referência", type: "date", section: "Rating Devedor", required: true },
    { name: "DataValidade", label: "Data validade", type: "date", section: "Rating Devedor", required: true },
    { name: "Fonte", label: "Fonte", section: "Rating Devedor", required: true },
    { name: "ScoreRating", label: "Score rating", type: "number", section: "Rating Devedor", required: true },
    { name: "Rating", label: "Rating", section: "Rating Devedor", required: true },
    { name: "Sinal", label: "Sinal", section: "Rating Devedor" },
    { name: "Perspectiva", label: "Perspectiva", section: "Rating Devedor" },
    { name: "AnaliseCreditoDevedor", label: "Análise de crédito do devedor", section: "Análise", multiline: true, fullWidthInDetails: true },
    { name: "Observacao", label: "Observação", section: "Análise", multiline: true, maxLength: 300, fullWidthInDetails: true },
]

const eventFields = [
    { name: "DataReferencia", label: "Data referência", type: "date", section: "Evento de crédito", required: true },
    { name: "EventoCreditoDevedor", label: "Evento de crédito", section: "Evento de crédito", multiline: true, required: true, fullWidthInDetails: true },
    { name: "ScoreEvento", label: "Score evento", type: "number", section: "Evento de crédito" },
]

const probabilityDefaultViewFields = [
    { name: "DataReferencia", label: "Data referência", type: "date", section: "Probabilidade de default", placeholder: "-" },
    { name: "PD", label: "PD", type: "number", displayType: "decimal", section: "Probabilidade de default", placeholder: "-" },
    { name: "Ticker", label: "Ticker", section: "Probabilidade de default", placeholder: "-" },
    { name: "Fonte", label: "Fonte", section: "Probabilidade de default", placeholder: "-" },
    { name: "EWMA", label: "EWMA", type: "number", displayType: "decimal", section: "Probabilidade de default", placeholder: "-" },
    { name: "FonteRating", label: "Fonte do rating", section: "Probabilidade de default", placeholder: "-" },
    { name: "RatingFinal", label: "Rating final", section: "Probabilidade de default", placeholder: "-" },
    { name: "MesBaseRating", label: "Mês-base do rating", section: "Probabilidade de default", placeholder: "-" },
]

const makeEditableHistory = ({ fields, createDefaults, request, interaction }) => ({
    fields,
    createDefaults,
    createFromLatest: ({ company, rows }) => copyLatestHistoryRow(rows) ?? createDefaults(company),
    request,
    interaction,
})

export const COMPANY_HISTORY_CONFIG = {
    rating: {
        title: "Histórico Rating Devedor",
        emptyText: "Esse devedor não possui rating.",
        getIdentifier: (company) => company?.CPFCNPJ,
        async load({ emissaoService, identifier }) {
            const response = await emissaoService.ratingDevedor("get", `?CPFCNPJ=${encodeURIComponent(identifier)}`)
            return sortByDate(getRows(response)).map((item) => ({ ...item, id: item.ID }))
        },
        editable: makeEditableHistory({
            fields: ratingFields,
            createDefaults: (company) => ({
                CPFCNPJ: company?.CPFCNPJ,
                Devedor: company?.RazaoSocial ?? company?.NomeFantasia ?? "",
                Setor: company?.Setor ?? "",
                SubSetor: company?.SubSetor ?? "",
            }),
            request: ({ emissaoService, method, row }) => emissaoService.ratingDevedor(method, method === "post" ? omitHistoryMetadata(row) : row.ID, method === "patch" ? row : null),
            interaction: { mode: "inline", addLabel: "ADICIONAR RATING" },
        }),
        columns: [
            { field: "DataReferencia", headerName: "Data referência", type: "date" },
            { field: "DataValidade", headerName: "Data validade", type: "date" },
            { field: "Fonte", headerName: "Fonte" },
            { field: "ScoreRating", headerName: "Score rating" },
            { field: "Rating", headerName: "Rating" },
            { 
                field: "Sinal", 
                headerName: "Sinal", 
                emptyValue: "",
                formatter: formatSinal,
            },
            { field: "Perspectiva", headerName: "Perspectiva" },
            { field: "Observacao", headerName: "Observação", minWidth: 240 },
            { field: "AnaliseCreditoDevedor", headerName: "Análise de crédito", minWidth: 280 },
        ],
    },
    event: {
        title: "Histórico Evento de Crédito Devedor",
        emptyText: "Esse devedor não possui eventos de crédito.",
        getIdentifier: (company) => company?.CPFCNPJ,
        async load({ emissaoService, identifier }) {
            const response = await emissaoService.eventoCreditoDevedor("get", `?CPFCNPJ=${encodeURIComponent(identifier)}`)
            return sortByDate(getRows(response)).map((item) => ({ ...item, id: item.ID }))
        },
        editable: makeEditableHistory({
            fields: eventFields,
            createDefaults: (company) => ({ CPFCNPJ: company?.CPFCNPJ }),
            request: ({ emissaoService, method, row }) => emissaoService.eventoCreditoDevedor(method, method === "post" ? omitHistoryMetadata(row) : row.ID, method === "patch" ? row : null),
            interaction: { mode: "inline", addLabel: "ADICIONAR REGISTRO" },
        }),
        columns: [
            { field: "DataReferencia", headerName: "Data referência", type: "date" },
            { field: "EventoCreditoDevedor", headerName: "Evento de crédito", minWidth: 420 },
            { field: "ScoreEvento", headerName: "Score evento" },
            { field: "CriadoEm", headerName: "Criado em", type: "date", adminOnly: true },
            { field: "AtualizadoEm", headerName: "Atualizado em", type: "date", adminOnly: true },
        ],
    },
    fact: {
        title: "Histórico Fato Relevante",
        emptyText: "Não há fato relevante disponível.",
        getIdentifier: (company) => company?.CodigoCVM,
        async load({ datalakeService, identifier }) {
            const response = await datalakeService.loadFatoRelevante(identifier)
            return sortByDate(getRows(response)).map((item) => ({ ...item, id: item.ID }))
        },
        columns: [
            { field: "DataReferencia", headerName: "Data referência", type: "date", minWidth: 100, flex: 0.8 },
            { field: "DataEntrega", headerName: "Data entrega", type: "date", minWidth: 100, flex: 0.8 },
            { field: "Categoria", headerName: "Categoria", minWidth: 140, flex: 1 },
            { field: "Assunto", headerName: "Assunto", minWidth: 220, flex: 1.5 },
            { field: "Resumo", headerName: "Resumo", minWidth: 420, flex: 3 },
            { field: "Link", headerName: "Documento", type: "link", minWidth: 90, flex: 0.5 },
        ],
    },
    probabilityDefault: {
        title: "Histórico Probabilidade de Default",
        emptyText: "Não há probabilidade de default disponível.",
        view: {
            mode: "readOnly",
            fields: probabilityDefaultViewFields,
        },
        getIdentifier: (company) => company?.RaizCNPJ,
        async load({ datalakeService, identifier }) {
            const response = await datalakeService.loadPD(identifier)
            return sortByDate(getRows(response)).map((item) => ({ ...item, id: item.ID ?? `${item.DataReferencia}-${item.PD}` }))
        },
        columns: [
            { field: "DataReferencia", headerName: "Data referência", type: "date" },
            {
                field: "PD",
                headerName: "PD",
                type: "number",
                align: "left",
                headerAlign: "left",
                valueGetter: ({ row }) => toNumericGridValue(row?.PD),
                formatter: formatPdGridValue,
            },
        ],
    },
}

export const formatHistoryValue = (value, type) => {
    if (value === null || value === undefined || value === "") return "-"
    if (type === "date") return formatDate(value)
    if (type === "decimal" || type === "number") return formatDecimalSeparator(value)
    return value
}
